// ============================================================================
// JARVISTRAVEL - RESPONSIVE IMAGERY
// Unsplash serves through imgix, so one photo URL becomes any width via the
// `w` parameter. We build a srcSet across sizes and let the browser pull the
// right resolution per viewport: a phone keeps the small file, a desktop hero
// gets the crisp one.
//
// The `ixid` parameter must survive every rewrite. It is how a view is
// reported back to the photographer, and dropping it is a guidelines
// violation — so the URL is rebuilt through the URL API, which preserves every
// parameter it does not explicitly set, rather than by string surgery.
//
// Anything that is not an Unsplash CDN URL returns undefined, and the caller's
// plain `src` is used unchanged.
// ============================================================================

import { isUnsplashImage } from './destinationImage';

const DEFAULT_WIDTHS = [480, 768, 1080, 1440, 2000];

/** Rebuild an Unsplash URL at a target width, preserving its other params.
 *
 *  `undefined` on failure rather than the original URL. Returning the input
 *  unchanged makes a failed rewrite indistinguishable from a successful one, and
 *  the caller pays for that: `imageSrcSet` would emit the unresized URL as a
 *  candidate at a width it is not, so the browser picks it believing it is
 *  sized. Unreachable today because `isUnsplashImage` already proved the URL
 *  parses, but a signature that cannot express failure is the reason a caller
 *  cannot handle it. */
function atWidth(url: string, w: number): string | undefined {
  try {
    const u = new URL(url);
    u.searchParams.set('w', String(w));
    u.searchParams.set('q', '80');
    u.searchParams.set('auto', 'format'); // WebP/AVIF where supported
    u.searchParams.set('fit', 'crop');
    return u.toString();
  } catch {
    return undefined;
  }
}

/** A single sized URL, for the plain `src`. */
export function imageAtWidth(url: string, width = 1080): string {
  // The plain src is the fallback everything else degrades to, so it keeps the
  // original URL when a rewrite is impossible: an unsized photo beats none.
  return isUnsplashImage(url) ? atWidth(url, width) ?? url : url;
}

/** A `srcSet` for an Unsplash photo; `undefined` for anything else. */
export function imageSrcSet(url?: string, widths: number[] = DEFAULT_WIDTHS): string | undefined {
  if (!url || !isUnsplashImage(url)) return undefined;
  // A variant that could not be rebuilt is dropped rather than emitted at a
  // width it is not. If every one drops there is no srcSet at all, and the
  // plain `src` carries the image — which is the intended fallback.
  const entries = widths
    .map((w) => { const at = atWidth(url, w); return at ? `${at} ${w}w` : null; })
    .filter((e): e is string => e !== null);
  return entries.length > 0 ? entries.join(', ') : undefined;
}

// Common `sizes` hints (rendered width per breakpoint) for the app's image slots.
export const IMG_SIZES = {
  /** Full-bleed hero capped by the content region. */
  hero: '(min-width: 1280px) 1320px, (min-width: 1024px) 1040px, 100vw',
  /** A card in a 1·2·3·4-up responsive grid. */
  card: '(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw',
  /** A capped reading-width hero (journal story). */
  reading: '(min-width: 768px) 768px, 100vw',
} as const;
